import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Mail } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { Input, Button } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureUpgradeModal } from '../../contexts/FeatureUpgradeModalContext';
import { useToast } from '../../hooks/useToast';
import { db } from '../../lib/firebase';
import type { EmailSettings } from '../../types/firestoreTypes';

const DEFAULT_SESSIONS_BEFORE_REVIEW = 5;

const EmailSettingsTab: React.FC = () => {
  const { t } = useTranslation();
  const { user, therapist } = useAuth();
  const { openUpgradeModal } = useFeatureUpgradeModal();
  const { addToast } = useToast();

  // State
  const [form, setForm] = useState<EmailSettings>({
    reviewRequestEnabled: false,
    reviewLink: '',
    sessionsBeforeReview: DEFAULT_SESSIONS_BEFORE_REVIEW,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [initialForm, setInitialForm] = useState<EmailSettings | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Feature gating
  const emailFeatureEnabled = Boolean(
    therapist?.packageFeaturesSnapshot?.EMAIL_NOTIFICATIONS
  );

  // Load initial settings
  useEffect(() => {
    if (therapist) {
      setIsLoading(true);
      setErrorKey(null);
      setErrorDetails(null);
      try {
        const settings = therapist.emailSettings || {
          reviewRequestEnabled: false,
          reviewLink: '',
          sessionsBeforeReview: DEFAULT_SESSIONS_BEFORE_REVIEW,
        };
        setForm(settings);
        setInitialForm(settings);
      } catch (err) {
        setErrorKey('settings.email.errors.loadFailed');
      } finally {
        setIsLoading(false);
      }
    }
  }, [therapist]);

  // Handlers
  const handleToggle = () => {
    if (!emailFeatureEnabled) {
      openUpgradeModal({
        featureNameKey: 'featureUpgradeModal.featureNames.emailNotifications',
        customMessageKey: 'featureUpgradeModal.messages.emailNotifications',
      });
      return;
    }
    setForm((prev) => ({ ...prev, reviewRequestEnabled: !prev.reviewRequestEnabled }));
    setEditMode(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    let val: any = value;
    if (type === 'number') {
      val = Math.max(1, Math.min(50, Number(value)));
    }
    setForm((prev) => ({ ...prev, [name]: val }));
    setEditMode(true);
    if (name === 'reviewLink') {
      setUrlError(null);
    }
  };

  const validateUrl = (url: string) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    if (!user || !therapist) return;
    if (form.reviewLink && !validateUrl(form.reviewLink)) {
      setUrlError(t('settings.email.form.errors.invalidUrl'));
      return;
    }
    setIsSaving(true);
    setErrorKey(null);
    setErrorDetails(null);
    try {
      await updateDoc(doc(db, 'therapists', user.uid), {
        emailSettings: form,
      });
      setInitialForm(form);
      setEditMode(false);
      addToast({
        type: 'success',
        message: t('settings.email.success.saved'),
      });
    } catch (err: any) {
      setErrorKey('settings.email.errors.saveFailed');
      setErrorDetails(err.message);
      addToast({
        type: 'error',
        message: t('settings.email.errors.saveFailed', { error: err.message }),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (initialForm) {
      setForm(initialForm);
      setEditMode(false);
      setUrlError(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7F56D9] border-t-transparent"></div>
        <span className="ml-3 text-gray-600">
          {t('settings.email.status.loading')}
        </span>
      </div>
    );
  }

  // Error state
  if (errorKey) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
        <p className="font-medium">{t('settings.email.errors.title')}</p>
        <p>{t(errorKey, { details: errorDetails })}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">
          {t('settings.email.heading')}
        </h2>
        <Button
          variant="primary"
          onClick={() => setEditMode(true)}
          leftIcon={<Mail className="w-4 h-4" />}
          disabled={editMode}
        >
          {t('settings.email.buttons.edit')}
        </Button>
      </div>

      {/* Card Layout */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
        {/* Toggle */}
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            {t('settings.email.form.reviewRequestEnabled')}
          </span>
          <button
            type="button"
            className={`inline-flex items-center px-2 py-1 rounded-full text-sm transition-colors
              ${form.reviewRequestEnabled ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
            onClick={handleToggle}
            disabled={!emailFeatureEnabled || !editMode}
          >
            {form.reviewRequestEnabled ? (
              <><Check className="w-4 h-4 mr-1" />{t('settings.email.form.enabled')}</>
            ) : (
              <><X className="w-4 h-4 mr-1" />{t('settings.email.form.disabled')}</>
            )}
          </button>
        </div>

        {/* Review Link */}
        <Input
          label={t('settings.email.form.reviewLink')}
          name="reviewLink"
          type="url"
          value={form.reviewLink || ''}
          onChange={handleChange}
          error={urlError || undefined}
          hint={t('settings.email.form.reviewLinkHint')}
          disabled={!editMode}
        />

        {/* Sessions Before Review */}
        <Input
          label={t('settings.email.form.sessionsBeforeReview')}
          name="sessionsBeforeReview"
          type="number"
          min={1}
          max={50}
          value={form.sessionsBeforeReview}
          onChange={handleChange}
          disabled={!editMode}
          hint={t('settings.email.form.sessionsBeforeReviewHint')}
        />

        {/* Action Buttons */}
        {editMode && (
          <div className="flex space-x-3 pt-2">
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={isSaving}
            >
              {t('settings.email.buttons.save')}
            </Button>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('settings.email.buttons.cancel')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettingsTab; 